import { BROWSE_SORTS, EXPERIENCE_BAND_VALUES, candidateBrowseSchema, uuidSchema, type BrowseSort, type ExperienceBand } from '@nest-recruit/shared';
import type { BrowseParams } from '../../api/candidate-browse-api';
import { CANDIDATE_PANEL_TABS, type CandidatePanelTab } from '../panel/CandidatePanel';

// CAND-01's address (06-qol §6): every filter, the sort, the page and the open pop-up live in the
// URL, so a link or a saved view shows a colleague the same list. Filters use the API's own
// names and lists are comma-joined, so the address IS the query.

export const PAGE_SIZE = 100;

export const FILTER_KEYS = [
  'text',
  'stages',
  'jobs',
  'tags',
  'starred',
  'skills',
  'skillMode',
  'experience',
  'education',
  'cities',
  'salaryMin',
  'salaryMax',
  /** DECISIONS-LOG 2026-10-19 Q6: `yes`, only for a viewer who may see salaries. */
  'overBudget',
  'maxNoticeDays',
  'reached',
  'sources',
  'from',
  'to',
  'addedFrom',
  'addedTo',
  'pools',
  'pool',
  'customField',
  'customValue',
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

export const LIST_KEYS = ['stages', 'jobs', 'tags', 'skills', 'experience', 'cities', 'reached', 'sources', 'pools'] as const satisfies readonly FilterKey[];
export type ListKey = (typeof LIST_KEYS)[number];

export const LOG_KINDS = ['email', 'call', 'note'] as const;
export type LogKind = (typeof LOG_KINDS)[number];

const DEFAULT_SORT: BrowseSort = 'last_activity';

export interface CandidateBrowseSearch extends Partial<Record<FilterKey, string>> {
  /** Only when it isn't the default, Last activity. */
  readonly sort?: BrowseSort;
  /** Only past the first page. */
  readonly page?: number;
  /** The pop-up's person, its tab, and the log kind a quick action opened it on. */
  readonly open?: string;
  readonly tab?: CandidatePanelTab;
  readonly log?: LogKind;
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/** A value the router may have parsed as a number (`?salaryMin=40000`) is still text to us. */
function textOf(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function isOneOf<T extends string>(options: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && options.some((option) => option === value);
}

/**
 * The route's reading of the address. A URL is input (06-qol §3): each filter is checked against
 * the API's own schema and a bad one is dropped rather than sent. `q` is the old page's search.
 */
export function readBrowseSearch(raw: Readonly<Record<string, unknown>>): CandidateBrowseSearch {
  const next: Mutable<CandidateBrowseSearch> = {};
  const source = raw.text === undefined && raw.q !== undefined ? { ...raw, text: raw.q } : raw;
  for (const key of FILTER_KEYS) {
    const value = textOf(source[key]);
    if (value === undefined) continue;
    if (key === 'skillMode' && value !== 'any') continue;
    if (candidateBrowseSchema.shape[key].safeParse(value).success) next[key] = value;
  }
  if (isOneOf(BROWSE_SORTS, raw.sort) && raw.sort !== DEFAULT_SORT) next.sort = raw.sort;
  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) next.page = page;
  if (typeof raw.open === 'string' && uuidSchema.safeParse(raw.open).success) {
    next.open = raw.open;
    if (isOneOf(CANDIDATE_PANEL_TABS, raw.tab)) next.tab = raw.tab;
    if (isOneOf(LOG_KINDS, raw.log)) next.log = raw.log;
  }
  return next;
}

/** The filters alone, as the address writes them: what a saved view keeps and "all matching" sends. */
export function filtersOf(search: CandidateBrowseSearch): Record<string, string> {
  const filters: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const value = search[key];
    if (value !== undefined) filters[key] = value;
  }
  return filters;
}

export function activeFilterCount(search: CandidateBrowseSearch): number {
  return FILTER_KEYS.filter((key) => key !== 'skillMode' && search[key] !== undefined).length;
}

/** The list query: the filters, the sort and the page, 50 a page. */
export function toBrowseParams(search: CandidateBrowseSearch): BrowseParams {
  return { ...filtersOf(search), sort: search.sort ?? DEFAULT_SORT, page: search.page ?? 1, pageSize: PAGE_SIZE };
}

/** The counts beside each option: the same filters, no page, and the skill list's own search. */
export function toFacetParams(search: CandidateBrowseSearch, skillSearch: string): BrowseParams {
  const trimmed = skillSearch.trim();
  return trimmed === '' ? filtersOf(search) : { ...filtersOf(search), skillSearch: trimmed };
}

export function listOf(search: CandidateBrowseSearch, key: ListKey): readonly string[] {
  const value = search[key];
  return value === undefined ? [] : value.split(',').filter((part) => part !== '');
}

/**
 * A filter changed: the list goes back to page 1 and the rest stays. Every change is made
 * through here, so no caller can forget the page.
 */
export function withFilter(search: CandidateBrowseSearch, key: FilterKey, value: string | undefined): CandidateBrowseSearch {
  const next: Mutable<CandidateBrowseSearch> = { ...search };
  delete next.page;
  const trimmed = value?.trim();
  if (trimmed === undefined || trimmed === '') delete next[key];
  else next[key] = trimmed;
  return next;
}

export function withList(search: CandidateBrowseSearch, key: ListKey, values: readonly string[]): CandidateBrowseSearch {
  return withFilter(search, key, values.length === 0 ? undefined : values.join(','));
}

export function toggleInList(search: CandidateBrowseSearch, key: ListKey, value: string): CandidateBrowseSearch {
  const current = listOf(search, key);
  return withList(search, key, current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
}

/** A stage tile: only that stage, or back to everyone when it was already the only one. */
export function pickOnlyStage(search: CandidateBrowseSearch, stage: string): CandidateBrowseSearch {
  const current = listOf(search, 'stages');
  const isOnly = current.length === 1 && current[0] === stage;
  return withList(search, 'stages', isOnly ? [] : [stage]);
}

/** "Clear all": every filter goes; the sort and an open pop-up stay. */
export function clearFilters(search: CandidateBrowseSearch): CandidateBrowseSearch {
  const next: Mutable<CandidateBrowseSearch> = {};
  if (search.sort) next.sort = search.sort;
  if (search.open) {
    next.open = search.open;
    if (search.tab) next.tab = search.tab;
    if (search.log) next.log = search.log;
  }
  return next;
}

/** The pop-up on one person; a log kind only comes with the Activity tab a quick action opened. */
export function withOpen(search: CandidateBrowseSearch, candidateId: string, tab?: CandidatePanelTab, log?: LogKind): CandidateBrowseSearch {
  const next: Mutable<CandidateBrowseSearch> = { ...withoutOpen(search), open: candidateId };
  if (tab !== undefined && tab !== 'cv') next.tab = tab;
  if (log !== undefined && tab === 'activity') next.log = log;
  return next;
}

export function withoutOpen(search: CandidateBrowseSearch): CandidateBrowseSearch {
  const next: Mutable<CandidateBrowseSearch> = { ...search };
  delete next.open;
  delete next.tab;
  delete next.log;
  return next;
}

export function withPage(search: CandidateBrowseSearch, page: number): CandidateBrowseSearch {
  const next: Mutable<CandidateBrowseSearch> = { ...search };
  if (page > 1) next.page = page;
  else delete next.page;
  return next;
}

/** A new order starts at page 1; the default order isn't written. */
export function withSort(search: CandidateBrowseSearch, sort: BrowseSort): CandidateBrowseSearch {
  const next: Mutable<CandidateBrowseSearch> = withPage(search, 1);
  if (sort === DEFAULT_SORT) delete next.sort;
  else next.sort = sort;
  return next;
}

/** A saved view's filters over the current sort and pop-up. */
export function applyFilters(search: CandidateBrowseSearch, filters: Readonly<Record<string, string>>): CandidateBrowseSearch {
  const read = readBrowseSearch(filters);
  return { ...clearFilters(search), ...filtersOf(read) };
}

export function sameFilters(a: Readonly<Record<string, string>>, b: Readonly<Record<string, string>>): boolean {
  const left = filtersOf(readBrowseSearch(a));
  const right = filtersOf(readBrowseSearch(b));
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every((key) => left[key] === right[key]);
}

// --- The old Advanced search's address (POOL-04), for links and bookmarks made before the merge ---

/** Ranking's bands as whole years, so an old "years from / to" picks the bands it overlaps. */
const BAND_YEARS: Readonly<Record<ExperienceBand, readonly [number, number]>> = {
  lt2: [0, 2],
  '2to4': [2, 4],
  '4to7': [4, 7],
  '7plus': [7, Number.POSITIVE_INFINITY],
};

function bandsBetween(minYears: number, maxYears: number): readonly ExperienceBand[] {
  return EXPERIENCE_BAND_VALUES.filter((band) => {
    const [from, below] = BAND_YEARS[band];
    return from <= maxYears && below > minYears;
  });
}

function yearsOf(value: unknown, fallback: number): number {
  const years = Number(textOf(value));
  return Number.isFinite(years) && years >= 0 ? years : fallback;
}

/** `/candidates/search?…` in the merged page's words; whatever doesn't fit is dropped by `readBrowseSearch`. */
export function legacySearchToBrowse(raw: Readonly<Record<string, unknown>>): CandidateBrowseSearch {
  const mapped: Record<string, unknown> = {
    text: raw.text,
    skills: raw.skills,
    skillMode: raw.mode,
    education: raw.education,
    cities: raw.city,
    salaryMin: raw.salaryMin,
    salaryMax: raw.salaryMax,
    maxNoticeDays: raw.notice,
    // The old "stages" was the last stage reached.
    reached: raw.stages,
    sources: raw.sources,
    from: raw.from,
    to: raw.to,
    pools: raw.pools,
    pool: raw.pool,
    customField: raw.field,
    customValue: raw.fieldValue,
  };
  if (raw.minYears !== undefined || raw.maxYears !== undefined) {
    const bands = bandsBetween(yearsOf(raw.minYears, 0), yearsOf(raw.maxYears, Number.POSITIVE_INFINITY));
    if (bands.length > 0 && bands.length < EXPERIENCE_BAND_VALUES.length) mapped.experience = bands.join(',');
  }
  return readBrowseSearch(mapped);
}
